import * as React from 'react';
import { flatMap, fromPairs, groupBy, isEqual } from 'lodash';
import Form from 'antd/lib/form';
import { NamePath } from 'antd/lib/form/interface';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Cascader from 'antd/lib/cascader';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Popover from 'antd/lib/popover';
import Typography from 'antd/lib/typography';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { PatternToken, SettingField, SettingFieldType } from '../types';
import TooltipInfoIcon from '../components/helperComponents/TooltipInfoIcon';
import { PatternTokenHelperTextComponent } from '../components/appSettings/PatternTokenHelperTextComponent';
import { runwayColors } from '../components/styles/globalStyles';
import { FieldType, filterOptionLabel, FormFieldData, renderFormField } from './formHelpers';

const INFO_ICON_GAP = 8;

// Keys are dot-separated payload paths, which antd writes back as nested objects
const getSettingFieldNamePath = (key: string): NamePath => (key.includes('.') ? key.split('.') : key);

export const formFieldsFromSettingFields = (settingFields: SettingField[]): FormFieldData[] => {
    return Object.values(groupBy(settingFields, 'key')).map((variants) => {
        const formField = formFieldFromSettingField(variants[0]);
        return variants.some((field) => field.visibleWhenEquals || field.visibleWhenAnySet) ?
                conditionalSettingField(formField, variants)
            :   formField;
    });
};

const formFieldFromSettingField = (settingField: SettingField): FormFieldData => {
    const isSwitch = settingField.type === SettingFieldType.Switch;
    // Switches and key/value lists show their own label and help text
    const hasInlineLabel = isSwitch || settingField.type === SettingFieldType.KeyValue;
    return {
        name: getSettingFieldNamePath(settingField.key),
        label: hasInlineLabel ? '' : settingField.label,
        initialValue: getSettingFieldInitialValue(settingField),
        fieldType: FieldType.Custom,
        rules: settingField.required ? [{ required: true }] : [],
        helpMessage: hasInlineLabel ? undefined : settingField.helpText,
        extraFormItemProps: isSwitch ? { valuePropName: 'checked' } : undefined,
        renderField: () => renderSettingFieldInput(settingField),
    };
};

// A cascader's value is [parent, child]; its form item holds the child
const getSettingFieldInitialValue = (settingField: SettingField): unknown => {
    if (settingField.type === SettingFieldType.Cascader) {
        return settingField.currentValue?.[1];
    }
    return settingField.currentValue ?? settingField.defaultValue;
};

const renderSettingFieldInput = (settingField: SettingField): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Switch:
            return (
                <Checkbox>
                    {settingField.label}
                    {settingField.helpText && (
                        <span style={{ marginInlineStart: INFO_ICON_GAP }}>
                            <TooltipInfoIcon tooltipContent={settingField.helpText} />
                        </span>
                    )}
                </Checkbox>
            );
        case SettingFieldType.Cascader:
            return <SettingFieldCascader settingField={settingField} />;
        case SettingFieldType.Select:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    style={{ width: '100%' }}
                />
            );
        case SettingFieldType.Input:
            return <Input autoComplete="off" />;
        case SettingFieldType.KeyValue:
            return (
                <KeyValueInput
                    itemLabel={settingField.label}
                    helpText={settingField.helpText}
                    patternTokens={settingField.patternTokens}
                />
            );
    }
};

// Form.Item passes the child value and onChange; the parent is written to its own key
const SettingFieldCascader: React.FC<{
    settingField: SettingField;
    value?: string;
    onChange?: (value: string | undefined) => void;
}> = ({ settingField, value, onChange }) => {
    const form = Form.useFormInstance();
    const parentPath = getSettingFieldNamePath(settingField.optionsBy?.field ?? '');
    const parentValue = Form.useWatch(parentPath);
    const options = getCascaderOptions(settingField);

    return (
        <>
            {/* Registers the parent, which not every definition has as its own field */}
            <Form.Item name={parentPath} initialValue={settingField.currentValue?.[0]} hidden noStyle>
                <Input />
            </Form.Item>
            <Cascader
                allowClear
                expandTrigger="hover"
                placeholder="Select"
                options={options}
                value={value && parentValue ? [parentValue, value] : undefined}
                onChange={(pair) => {
                    form.setFieldValue(parentPath, pair?.[0]);
                    onChange?.(pair?.[1] as string | undefined);
                }}
                style={{ width: '100%' }}
            />
        </>
    );
};

// Parents without children can't complete a pair, so they're disabled
const getCascaderOptions = (settingField: SettingField) => {
    const childOptionsByParent = settingField.optionsBy?.options ?? {};
    return (settingField.options ?? []).map((parentOption) => {
        const children = childOptionsByParent[String(parentOption.value)] ?? [];
        return { ...parentOption, children, disabled: children.length === 0 };
    });
};

// Build Distro bucket rules save the picked workflow as one plain value ([parent, workflow] for a cascader),
// so they get simple inputs built from the release candidate workflow and subworkflow fields
export const getBuildDistroWorkflowPickerFields = (settingFields: SettingField[]): FormFieldData[] => {
    const workflowVariants = settingFields.filter((field) => field.key === 'workflowId');
    const workflowField =
        workflowVariants.find((field) => field.type === SettingFieldType.Cascader) ??
        workflowVariants.find((field) => field.type === SettingFieldType.Select) ??
        workflowVariants[0];
    const subWorkflowField = settingFields.find((field) => field.key === 'subWorkflowId');
    return [workflowField, subWorkflowField]
        .filter((field): field is SettingField => !!field)
        .map((field) => ({
            label: field.label,
            name: field.key,
            fieldType: FieldType.Custom,
            renderField: (_: unknown, extraProperties?: object) =>
                renderBuildDistroWorkflowInput(field, extraProperties),
        }));
};

const renderBuildDistroWorkflowInput = (settingField: SettingField, extraProperties?: object): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Cascader:
            return (
                <Cascader
                    allowClear
                    expandTrigger="hover"
                    placeholder="Select"
                    options={getCascaderOptions(settingField)}
                    {...extraProperties}
                />
            );
        case SettingFieldType.Select:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    {...extraProperties}
                />
            );
        default:
            return <Input autoComplete="off" {...extraProperties} />;
    }
};

type KeyValueRow = [key: string, value: string];

// Edits a map as rows; rows without a key are kept while typing but left out of the value
const KeyValueInput: React.FC<{
    itemLabel: string;
    helpText?: string;
    patternTokens?: PatternToken[];
    value?: Record<string, string>;
    onChange?: (value: Record<string, string>) => void;
}> = ({ itemLabel, helpText, patternTokens, value, onChange }) => {
    const [rows, setRows] = React.useState<KeyValueRow[]>(() => Object.entries(value ?? {}));
    // Resyncs when the form sets the value (reset, new data), but keeps rows the user is still typing a key for
    React.useEffect(() => {
        setRows((currentRows) =>
            isEqual(getKeyValueRowsValue(currentRows), value ?? {}) ? currentRows : Object.entries(value ?? {}),
        );
    }, [value]);

    const hasRows = rows.length > 0;
    return (
        <Space direction="vertical" style={{ width: '100%', paddingBottom: 16 }} size={16}>
            {rows.map(([rowKey, rowValue], index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Input
                        placeholder="Key"
                        value={rowKey}
                        onChange={(event) =>
                            setKeyValueRows(
                                rows.map((row, i) => (i === index ? [event.target.value, row[1]] : row)),
                                setRows,
                                onChange,
                            )
                        }
                    />
                    <Input
                        placeholder="Value"
                        value={rowValue}
                        onChange={(event) =>
                            setKeyValueRows(
                                rows.map((row, i) => (i === index ? [row[0], event.target.value] : row)),
                                setRows,
                                onChange,
                            )
                        }
                    />
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                        onClick={() =>
                            setKeyValueRows(
                                rows.filter((_, i) => i !== index),
                                setRows,
                                onChange,
                            )
                        }
                    />
                </div>
            ))}
            {hasRows && patternTokens && (
                <Typography.Text type="secondary">
                    Tokenized patterns are supported.
                    <Popover content={<PatternTokenHelperTextComponent availableTokens={patternTokens} />}>
                        <InfoCircleOutlined
                            style={{ color: runwayColors.darkTextGrey, marginInlineStart: INFO_ICON_GAP }}
                        />
                    </Popover>
                </Typography.Text>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: INFO_ICON_GAP, width: 'fit-content' }}>
                <Button
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => setKeyValueRows([...rows, ['', '']], setRows, onChange)}
                    size="small"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                    {hasRows ? `Add another ${itemLabel.toLowerCase()}` : `Add ${itemLabel.toLowerCase()}`}
                </Button>
                {helpText && <TooltipInfoIcon tooltipContent={helpText} />}
            </div>
        </Space>
    );
};

const setKeyValueRows = (
    rows: KeyValueRow[],
    setRows: (rows: KeyValueRow[]) => void,
    onChange?: (value: Record<string, string>) => void,
) => {
    setRows(rows);
    onChange?.(getKeyValueRowsValue(rows));
};

const getKeyValueRowsValue = (rows: KeyValueRow[]): Record<string, string> => {
    return fromPairs(rows.filter(([rowKey]) => rowKey !== ''));
};

// One Form.Item owns each payload key, even when its input changes with another setting.
const conditionalSettingField = (formField: FormFieldData, variants: SettingField[]): FormFieldData => {
    const dependencies = [
        ...new Set(
            flatMap(variants, (field) => [
                ...Object.keys(field.visibleWhenEquals ?? {}),
                ...(field.visibleWhenAnySet ?? []),
            ]),
        ),
    ].map(getSettingFieldNamePath);

    return {
        ...formField,
        fieldType: FieldType.Custom,
        skipFormItemWrapper: true,
        renderField: () => (
            <Form.Item noStyle dependencies={dependencies}>
                {({ getFieldValue }) => {
                    const activeField = variants.find((field) => isSettingFieldVisible(field, getFieldValue));
                    const field = formFieldFromSettingField(activeField ?? variants[0]);
                    return renderFormField({
                        ...field,
                        dependencies,
                        rules: activeField ? field.rules : [],
                        // Keep hidden values registered for submission, without running an inactive input's effects.
                        extraFormItemProps: { ...field.extraFormItemProps, hidden: !activeField },
                        renderField: activeField ? field.renderField : () => <Input />,
                    });
                }}
            </Form.Item>
        ),
    };
};

const isSettingFieldVisible = (settingField: SettingField, getFieldValue: (name: NamePath) => unknown): boolean => {
    const matchesValues = Object.entries(settingField.visibleWhenEquals ?? {}).every(([key, expected]) =>
        isEqual(getFieldValue(getSettingFieldNamePath(key)), expected),
    );
    const visibleWhenAnySet = settingField.visibleWhenAnySet ?? [];
    const hasSetValue =
        visibleWhenAnySet.length === 0 ||
        visibleWhenAnySet.some((key) => !isEmptySettingValue(getFieldValue(getSettingFieldNamePath(key))));
    return matchesValues && hasSetValue;
};

const isEmptySettingValue = (value: unknown): boolean => {
    if (value == null || value === '') return true;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};
